import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { HealthHistoryPoint, HealthStatus } from 'src/app/core/services/platform-health.service';

interface StripCell {
  date: string;
  status: HealthStatus;
}

// Worse status wins when more than one point falls on the same UTC day.
const SEVERITY_BY_STATUS: Record<HealthStatus, number> = {
  unknown: 0,
  ok: 1,
  degraded: 2,
  down: 3,
};

const CELL_CLASS_BY_STATUS: Record<HealthStatus, string> = {
  ok: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-300 dark:bg-gray-600',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-status-strip',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex gap-px">
      <span
        *ngFor="let c of cells()"
        [attr.data-cell]="c.date"
        [attr.data-status]="c.status"
        [class]="cellClass[c.status]"
        [title]="c.date + ' · ' + c.status"
        class="h-4 w-1.5 rounded-sm"
      ></span>
    </div>
  `,
})
export class StatusStripComponent {
  readonly points = input<HealthHistoryPoint[]>([]);
  readonly days = input(90);

  readonly cellClass = CELL_CLASS_BY_STATUS;

  // One cell per day from (today - (days-1)) to today (UTC), coloured by that day's worst status.
  readonly cells = computed<StripCell[]>(() => {
    const todayStart = this.startOfUtcDay(new Date());
    const statusByDay = this.worstStatusByDay(this.points());
    const cellCount = this.days();
    const result: StripCell[] = [];
    for (let i = cellCount - 1; i >= 0; i--) {
      const dayStart = new Date(todayStart.getTime() - i * MS_PER_DAY);
      const key = dayStart.toISOString().slice(0, 10);
      result.push({ date: key, status: statusByDay.get(key) ?? 'unknown' });
    }
    return result;
  });

  private startOfUtcDay(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  private worstStatusByDay(points: HealthHistoryPoint[]): Map<string, HealthStatus> {
    const statusByDay = new Map<string, HealthStatus>();
    for (const point of points) {
      const key = this.startOfUtcDay(new Date(point.bucket_start)).toISOString().slice(0, 10);
      const current = statusByDay.get(key);
      if (!current || SEVERITY_BY_STATUS[point.status] > SEVERITY_BY_STATUS[current]) {
        statusByDay.set(key, point.status);
      }
    }
    return statusByDay;
  }
}
